// Service details database with complete information
export const serviceDetails: Record<string, any> = {
  sin_bank_msp: {
    fullName: "Essential First Steps",
    sections: [
      {
        title: "1. SIN Number (Social Insurance Number)",
        whatYouNeed: [
          "Valid passport or travel document",
          "Immigration documents (COPR, work permit, study permit, or refugee documents)",
          "Proof of address (optional but helpful)"
        ],
        howToApply: [
          "Go to nearest Service Canada office (no appointment needed)",
          "Fill out SIN application form at the office",
          "Submit original documents (no photocopies)",
          "Receive your SIN number immediately (10-30 minutes)"
        ],
        locations: [
          { name: "Service Canada - Vancouver", address: "125 10th Ave E, Vancouver, BC", phone: "1-800-622-6232", map: "https://maps.google.com/?q=125+10th+Ave+E+Vancouver" },
          { name: "Service Canada - Surrey", address: "9850 King George Blvd #100, Surrey, BC", phone: "1-800-622-6232", map: "https://maps.google.com/?q=9850+King+George+Blvd+Surrey" },
          { name: "Service Canada - Burnaby", address: "4475 Wayburne Dr, Burnaby, BC", phone: "1-800-622-6232", map: "https://maps.google.com/?q=4475+Wayburne+Dr+Burnaby" }
        ],
        hours: "Monday-Friday: 8:30am-4:00pm (closed on holidays)",
        cost: "FREE",
        timeNeeded: "10-30 minutes at office",
        importantNotes: [
          "⚠️ You MUST have a SIN to work legally in Canada",
          "⚠️ Bring original documents, not photocopies",
          "⚠️ This is a FREE service - never pay anyone for SIN application"
        ]
      },
      {
        title: "2. Opening a Bank Account",
        whatYouNeed: [
          "Two pieces of ID (passport, immigration documents, or BC Services Card)",
          "Proof of address (rental agreement, utility bill, or letter from organization)",
          "Small initial deposit (usually $25-$100)"
        ],
        howToApply: [
          "Choose a bank (RBC, TD, Scotiabank, CIBC, BMO have newcomer programs)",
          "Book appointment online or walk into a branch",
          "Bring required documents",
          "Complete application forms with banker",
          "Receive debit card (same day or by mail)"
        ],
        locations: [
          { name: "Multiple Banks", address: "Find nearest branch at each bank's website", phone: "Varies by bank" }
        ],
        hours: "Typically Mon-Fri: 9:30am-5pm, Sat: 10am-4pm (varies by branch)",
        cost: "Many banks offer FREE accounts for newcomers (first 1-2 years)",
        timeNeeded: "30-60 minutes at bank",
        importantNotes: [
          "💡 Ask about newcomer banking packages (often fee-free)",
          "💡 You can open account before getting SIN, but provide it later",
          "💡 Compare different banks - fees and services vary",
          "💡 Credit unions often have lower fees than major banks"
        ]
      },
      {
        title: "3. MSP (BC Health Insurance)",
        whatYouNeed: [
          "Immigration documents (COPR, work permit, or study permit)",
          "Proof of BC residency (rental agreement or letter)",
          "SIN number (helpful but not required)"
        ],
        howToApply: [
          "Apply online at gov.bc.ca/msp or by mail",
          "Fill out MSP enrollment form",
          "Submit copies of required documents",
          "Wait for your BC Services Card (arrives by mail in 2-3 weeks)"
        ],
        locations: [
          { name: "Online Application", address: "www.gov.bc.ca/msp", phone: "1-800-663-7100 (for questions)" }
        ],
        hours: "Apply online 24/7. Phone support: Mon-Fri 8am-8pm",
        cost: "FREE (no monthly premiums since 2020)",
        timeNeeded: "30 minutes to apply online, 2-3 weeks to receive card",
        importantNotes: [
          "⚠️ Coverage may start after 3-month wait period (check if you qualify for immediate coverage)",
          "✅ Refugees and some work permit holders get immediate coverage",
          "✅ Apply as soon as you arrive in BC",
          "✅ Your BC Services Card is also your provincial ID card"
        ]
      }
    ]
  },
  linc_beginner: {
    fullName: "Free English Classes (LINC)",
    whatYouNeed: [
      "Immigration documents (PR card, COPR, refugee documents, or work permit)",
      "Language assessment (providers will arrange this for free)"
    ],
    howToApply: [
      "Contact a LINC provider near you (MOSAIC, ISSofBC, or SUCCESS)",
      "Book a FREE language assessment",
      "Register for classes at your level",
      "Start classes (full-time or part-time options available)"
    ],
    locations: [
      { name: "MOSAIC - Vancouver", address: "1720 Grant St, Vancouver, BC", phone: "604-254-9626", map: "https://maps.google.com/?q=1720+Grant+St+Vancouver" },
      { name: "MOSAIC - Surrey", address: "7000 Minoru Blvd, Surrey, BC", phone: "604-596-7722", map: "https://maps.google.com/?q=7000+Minoru+Blvd+Surrey" },
      { name: "ISSofBC - Burnaby", address: "5th Floor, 2610 Library Lane, Burnaby, BC", phone: "604-684-2561", map: "https://maps.google.com/?q=2610+Library+Lane+Burnaby" },
      { name: "ISSofBC - Vancouver", address: "501-2929 Commercial Dr, Vancouver, BC", phone: "604-684-2561", map: "https://maps.google.com/?q=2929+Commercial+Dr+Vancouver" },
      { name: "SUCCESS - Vancouver", address: "Multiple locations", phone: "604-408-7228", map: "https://successbc.ca/eng/locations" }
    ],
    hours: "Classes available mornings, afternoons, and evenings",
    cost: "FREE (government-funded)",
    timeNeeded: "Classes: 3-4 hours/day, 3-5 days/week (flexible schedules)",
    importantNotes: [
      "👶 Free childcare available during classes at most locations",
      "✅ Can join at any time - classes start frequently",
      "✅ Different streams: general, employment-focused, or academic",
      "🚌 Transportation support may be available"
    ]
  },
  workbc_employment: {
    fullName: "WorkBC Employment Services",
    whatYouNeed: [
      "Valid work authorization in Canada",
      "SIN number",
      "Resume (they can help you create one if you don't have it)"
    ],
    howToApply: [
      "Find your nearest WorkBC Centre at workbc.ca",
      "Drop in during business hours or call to book appointment",
      "Meet with an employment counsellor",
      "Create an action plan for your job search"
    ],
    locations: [
      { name: "WorkBC Centre - Downtown Vancouver", address: "789 W Pender St #200, Vancouver, BC", phone: "604-718-5415", map: "https://maps.google.com/?q=789+W+Pender+St+Vancouver" },
      { name: "WorkBC Centre - Surrey", address: "13450 102 Ave #200, Surrey, BC", phone: "604-585-2788", map: "https://maps.google.com/?q=13450+102+Ave+Surrey" },
      { name: "WorkBC Centre - Burnaby", address: "4980 Kingsway #200, Burnaby, BC", phone: "604-638-4700", map: "https://maps.google.com/?q=4980+Kingsway+Burnaby" },
      { name: "Find All WorkBC Centres", address: "Visit workbc.ca/WorkBC-Centres", phone: "1-877-952-6914" }
    ],
    hours: "Monday-Friday: 8:30am-4:30pm (varies by location)",
    cost: "FREE",
    timeNeeded: "Initial appointment: 1 hour, ongoing support as needed",
    importantNotes: [
      "💻 Free access to computers, printers, phones for job search",
      "📚 Workshops on resume writing, interview skills, networking",
      "💰 May qualify for funding for training or certifications",
      "🤝 Connections to employers actively hiring"
    ]
  },
  settlement_services: {
    fullName: "Settlement Information & Orientation",
    whatYouNeed: [
      "Immigration documents only"
    ],
    howToApply: [
      "Contact ISSofBC, MOSAIC, or SUCCESS",
      "Book an orientation session (individual or group)",
      "Attend session to learn about BC systems and services"
    ],
    locations: [
      { name: "ISSofBC - Main Office", address: "501-2929 Commercial Dr, Vancouver, BC", phone: "604-684-2561", map: "https://maps.google.com/?q=2929+Commercial+Dr+Vancouver" },
      { name: "MOSAIC - Main Office", address: "1720 Grant St, Vancouver, BC", phone: "604-254-9626", map: "https://maps.google.com/?q=1720+Grant+St+Vancouver" },
      { name: "SUCCESS - Main Office", address: "28 W Pender St, Vancouver, BC", phone: "604-408-7228", map: "https://maps.google.com/?q=28+W+Pender+St+Vancouver" }
    ],
    hours: "Monday-Friday: 9am-5pm (appointments recommended)",
    cost: "FREE",
    timeNeeded: "1-2 hours for orientation session",
    importantNotes: [
      "🗣️ Available in multiple languages",
      "📋 Covers housing, health, education, legal rights, transportation",
      "❓ Can ask any questions about life in Canada",
      "🔗 Get referrals to other services you need"
    ]
  },
  housing_support: {
    fullName: "Housing Search Support",
    whatYouNeed: [
      "Information about your housing needs and budget"
    ],
    howToApply: [
      "Contact ISSofBC or MOSAIC housing services",
      "Speak with a housing support worker",
      "Get support searching for housing online",
      "Learn about tenant rights and responsibilities in BC"
    ],
    locations: [
      { name: "ISSofBC Housing Services", address: "Contact main office: 604-684-2561", phone: "604-684-2561" },
      { name: "MOSAIC Housing Services", address: "Contact main office: 604-254-9626", phone: "604-254-9626" }
    ],
    hours: "Monday-Friday: 9am-5pm",
    cost: "FREE",
    timeNeeded: "Ongoing support as needed",
    importantNotes: [
      "🏠 Help understanding rental listings and websites",
      "📜 Learn about Residential Tenancy Act (tenant rights in BC)",
      "✅ Tips for viewing apartments and signing leases",
      "💰 Information about rental assistance programs if eligible"
    ]
  },
  family_programs: {
    fullName: "Family Support Programs",
    whatYouNeed: [
      "Information about your family's needs"
    ],
    howToApply: [
      "Contact ISSofBC or SUCCESS family programs",
      "Discuss your family's specific needs",
      "Get connected to appropriate programs and services"
    ],
    locations: [
      { name: "ISSofBC Family Programs", address: "Multiple locations - call 604-684-2561", phone: "604-684-2561" },
      { name: "SUCCESS Family Services", address: "Multiple locations - call 604-408-7228", phone: "604-408-7228" }
    ],
    hours: "Varies by program",
    cost: "FREE",
    timeNeeded: "Varies by program",
    importantNotes: [
      "🏫 Help with school registration for children",
      "👨‍👩‍👧 Parenting programs and workshops",
      "🎓 Youth programs for teenagers",
      "⏰ After-school programs available at some locations"
    ]
  },
  counselling: {
    fullName: "Mental Health & Counselling Support",
    whatYouNeed: [
      "Immigration documents",
      "Willingness to participate in counselling"
    ],
    howToApply: [
      "Contact MOSAIC or ISSofBC counselling services",
      "Request counselling support",
      "Schedule appointment with a counsellor",
      "Sessions available in multiple languages"
    ],
    locations: [
      { name: "MOSAIC Counselling", address: "1720 Grant St, Vancouver, BC", phone: "604-254-9626", map: "https://maps.google.com/?q=1720+Grant+St+Vancouver" },
      { name: "ISSofBC Counselling", address: "Call for appointment: 604-684-2561", phone: "604-684-2561" }
    ],
    hours: "By appointment only",
    cost: "FREE (short-term counselling, usually 6-8 sessions)",
    timeNeeded: "50-60 minutes per session",
    importantNotes: [
      "🔒 Confidential services",
      "❤️ Specialized trauma support for refugees",
      "🏳️‍🌈 2SLGBTQI+ support available at MOSAIC",
      "🔗 Can provide referrals for longer-term support if needed"
    ]
  },
  refugee_support: {
    fullName: "Refugee Settlement Support (RAP)",
    whatYouNeed: [
      "Refugee status documents"
    ],
    howToApply: [
      "Government-assisted refugees: ISSofBC will contact you when you arrive",
      "Refugee claimants: Contact ISSofBC or MOSAIC directly",
      "Support starts immediately upon arrival for RAP clients"
    ],
    locations: [
      { name: "ISSofBC Refugee Services", address: "501-2929 Commercial Dr, Vancouver, BC", phone: "604-684-2561", map: "https://maps.google.com/?q=2929+Commercial+Dr+Vancouver" },
      { name: "MOSAIC Refugee Services", address: "1720 Grant St, Vancouver, BC", phone: "604-254-9626", map: "https://maps.google.com/?q=1720+Grant+St+Vancouver" }
    ],
    hours: "Monday-Friday: 9am-5pm, emergency support available",
    cost: "FREE",
    timeNeeded: "Up to 1 year of support",
    importantNotes: [
      "🏠 Help with immediate needs (food, clothing, housing)",
      "💰 Financial support for first year (for government-assisted refugees)",
      "📚 Life skills orientation and training",
      "🔗 Connection to all essential services"
    ]
  }
};
